import { useState, useEffect } from "react";
import "./Expenses.css";
import "./CRM.css"; // Reuse common styles
import dataService from "../services/dataService";
import authService from "../services/authService";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { formatCurrency } from "../utils/formatCurrency";
import { Eye, FileEdit, Trash2, Search, Plus, Download } from "lucide-react";

function ExpensesScreen() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [formData, setFormData] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await dataService.getExpenses();
      setExpenses(response.data.data);
    } catch (error) {
      console.error("Failed to fetch expenses", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleAddExpenses = () => {
    navigate("/expenses/add");
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      
      const headers = rows[0].map(h => h.trim().replace(/"/g, ''));
      const idIdx = headers.findIndex(h => h.toLowerCase().includes('id') || h.toLowerCase().includes('number'));
      const categoryIdx = headers.findIndex(h => h.toLowerCase().includes('category'));
      const descIdx = headers.findIndex(h => h.toLowerCase().includes('description') || h.toLowerCase().includes('desc'));
      const dateIdx = headers.findIndex(h => h.toLowerCase().includes('date'));
      const amountIdx = headers.findIndex(h => h.toLowerCase().includes('amount') || h.toLowerCase().includes('total'));
      const vendorIdx = headers.findIndex(h => h.toLowerCase().includes('vendor') || h.toLowerCase().includes('supplier'));
      const statusIdx = headers.findIndex(h => h.toLowerCase().includes('status'));

      const newExpenses = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2) continue;
        
        const amountVal = parseFloat((row[amountIdx] || '0').replace(/"/g, '').trim()) || 0;
        const rawCategory = (row[categoryIdx] || 'Others').replace(/"/g, '').trim();
        const categoryVal = rawCategory.charAt(0).toUpperCase() + rawCategory.slice(1);

        newExpenses.push({
          expenseId: (row[idIdx] || `EXP-${Date.now().toString().slice(-4)}-${i}`).replace(/"/g, '').trim(),
          category: categoryVal,
          description: (row[descIdx] || 'Imported Expense').replace(/"/g, '').trim(),
          date: (row[dateIdx] || new Date().toISOString().split('T')[0]).replace(/"/g, '').trim(),
          amount: amountVal,
          vendor: (row[vendorIdx] || '').replace(/"/g, '').trim(),
          status: (row[statusIdx] || 'Paid').replace(/"/g, '').trim()
        });
      }

      if (newExpenses.length > 0) {
        addToast(`Importing ${newExpenses.length} expenses...`, "success");
        try {
          for (const exp of newExpenses) {
            await dataService.addExpense(exp);
          }
          fetchExpenses();
          addToast("Import successful!", "success");
        } catch (err) {
          addToast("Error during import", "error");
        }
      }
    };
    reader.readAsText(file);
  };

  const handleView = (expense) => {
    setSelectedExpense(expense);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEdit = (expense) => {
    navigate(`/expenses/edit/${expense._id}`);
  };

  const handleDeleteClick = (expense) => {
    setItemToDelete(expense);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dataService.deleteExpense(itemToDelete._id);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchExpenses();
      addToast("Expense deleted successfully", "success");
    } catch (error) {
      addToast("Failed to delete expense: " + error.message, "error");
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dataService.updateExpense(selectedExpense._id, formData);
      setIsModalOpen(false);
      fetchExpenses();
      addToast("Expense updated successfully", "success");
    } catch (error) {
      addToast("Failed to update expense: " + error.message, "error");
    }
  };

  const getChartData = () => {
    const categories = {};
    expenses.forEach(exp => {
      categories[exp.category] = (categories[exp.category] || 0) + exp.amount;
    });
    return Object.keys(categories).map(name => ({ name, value: categories[name] }));
  };

  const COLORS = ["#2563eb", "#16a34a", "#ca8a04", "#dc2626", "#6b7280"];

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Expenses Management</h1>
          <p>Track and manage business expenses</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="secondary-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Import CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          {authService.hasPermission('expenses', 'createExpenses') && (
            <button className="primary-btn" onClick={handleAddExpenses}>
              <Plus size={18} /> Add Expense
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Expenses</span>
            <h2 className="stat-value">{formatCurrency(expenses.reduce((acc, e) => acc + e.amount, 0))}</h2>
          </div>
        </div>
        <div className="stat-card month">
          <div className="stat-info">
            <span className="stat-label">This Month</span>
            <h2 className="stat-value">{formatCurrency(expenses.filter(e => new Date(e.date).getMonth() === new Date().getMonth()).reduce((acc, e) => acc + e.amount, 0))}</h2>
          </div>
        </div>
        <div className="stat-card pending">
          <div className="stat-info">
            <span className="stat-label">Pending</span>
            <h2 className="stat-value" style={{ color: '#EA580C' }}>{formatCurrency(expenses.filter(e => e.status === 'Pending').reduce((acc, e) => acc + e.amount, 0))}</h2>
          </div>
        </div>
        <div className="stat-card categories">
          <div className="stat-info">
            <span className="stat-label">Total Categories</span>
            <h2 className="stat-value">{new Set(expenses.map(e => e.category)).size}</h2>
          </div>
        </div>
      </div>

      <div className="graph-container" style={{ display: 'flex', gap: '20px', marginTop: '32px' }}>
        <div className="content-card" style={{ flex: 1 }}>
          <div className="card-controls">
            <h2 className="font-bold">Expenses by Category</h2>
          </div>
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}>
            <PieChart width={320} height={260}>
              <Pie
                data={getChartData()}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
              >
                {getChartData().map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `AED ${value}`} />
              <Legend />
            </PieChart>
          </div>
        </div>

        <div className="content-card" style={{ flex: 1 }}>
          <div className="card-controls">
            <h2 className="font-bold">Profit & Loss Overview</h2>
          </div>
          <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 4px 0' }}>Total Revenue</p>
              <h3 style={{ fontSize: '24px', margin: 0 }}>AED 92,400</h3>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 4px 0' }}>Total Expenses</p>
              <h3 style={{ fontSize: '24px', margin: 0, color: '#DC2626' }}>{formatCurrency(expenses.reduce((acc, e) => acc + e.amount, 0))}</h3>
            </div>
            <div>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 4px 0' }}>Net Profit</p>
              <h3 style={{ fontSize: '24px', margin: 0, color: '#00A859' }}>{formatCurrency(92400 - expenses.reduce((acc, e) => acc + e.amount, 0))}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <h2 className="font-bold">Recent Expenses</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search by ID or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Loading expenses...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Expense ID</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses
                  .filter(e =>
                    e.expenseId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    e.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (e.description && e.description.toLowerCase().includes(searchTerm.toLowerCase()))
                  )
                  .map((expense) => (
                    <tr key={expense._id}>
                      <td><span className="font-bold">{expense.expenseId}</span></td>
                      <td><span className="category-pill">{expense.category}</span></td>
                      <td>{expense.description}</td>
                      <td>{new Date(expense.date).toLocaleDateString()}</td>
                      <td><span className="font-bold">{formatCurrency(expense.amount)}</span></td>
                      <td>
                        <span className={`status-pill ${expense.status.toLowerCase()}`}>
                          {expense.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="action-icons">
                          <button className="icon-btn" title="View" onClick={() => handleView(expense)}>
                            <Eye size={16} />
                          </button>

                          {authService.hasPermission('expenses', 'editExpenses') && (
                            <button className="icon-btn success" title="Edit" onClick={() => handleEdit(expense)}>
                              <FileEdit size={16} />
                            </button>
                          )}

                          {authService.hasPermission('expenses', 'deleteExpenses') && (
                            <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteClick(expense)}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {expenses.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No expenses found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === "view" ? "Expense Details" : "Edit Expense"}
        footer={
          modalMode === "edit" ? (
            <>
              <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="primary-btn" onClick={handleUpdateSubmit}>Save Changes</button>
            </>
          ) : (
            <button className="primary-btn" onClick={() => setIsModalOpen(false)}>Close</button>
          )
        }
      >
        {modalMode === "view" && selectedExpense && (
          <div className="detail-view">
            <div className="detail-item">
              <span className="detail-label">Expense ID</span>
              <span className="detail-value">{selectedExpense.expenseId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Category</span>
              <span className="category-pill">{selectedExpense.category}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Description</span>
              <span className="detail-value">{selectedExpense.description}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Date</span>
              <span className="detail-value">{new Date(selectedExpense.date).toLocaleDateString()}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Amount</span>
              <span className="detail-value" style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(selectedExpense.amount)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Vendor</span>
              <span className="detail-value">{selectedExpense.vendor || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-pill ${selectedExpense.status?.toLowerCase()}`}>
                {selectedExpense.status}
              </span>
            </div>
          </div>
        )}

        {modalMode === "edit" && (
          <form className="modal-form" onSubmit={handleUpdateSubmit}>
            <div className="form-group">
              <label>Description</label>
              <input name="description" value={formData.description || ""} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select name="category" value={formData.category || ""} onChange={handleFormChange}>
                <option value="Raw Material">Raw Material</option>
                <option value="Supplies">Supplies</option>
                <option value="Utilities">Utilities</option>
                <option value="Logistics">Logistics</option>
                <option value="Marketing">Marketing</option>
                <option value="Others">Others</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status || ""} onChange={handleFormChange}>
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
                <option value="Void">Void</option>
              </select>
            </div>
            <div className="form-group">
              <label>Amount</label>
              <input type="number" name="amount" value={formData.amount || 0} onChange={handleFormChange} />
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            <button className="primary-btn btn-danger" onClick={confirmDelete}>Delete Expense</button>
          </>
        }
      >
        <div className="delete-confirm-view">
          <div className="delete-icon-wrapper">
            <Trash2 size={32} />
          </div>
          <h3>Are you sure?</h3>
          <p>You are about to delete expense <strong>{itemToDelete?.expenseId}</strong>. This action cannot be undone.</p>
        </div>
      </Modal>
    </div>
  );
}

export default ExpensesScreen;
